"use client";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const CTAButton = styled("button")(({ theme }) => ({
  background: "#7C3AED",
  color: "#fff",
  border: "none",
  borderRadius: "50px",
  padding: "12px 20px",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontFamily: "var(--font-outfit)",
  whiteSpace: "nowrap",
  transition: "all 0.3s ease-in-out",

  [theme.breakpoints.down("md")]: {
    padding: "9px 15px",
    fontSize: "13px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "7px 12px",
    fontSize: "12px",
    gap: "4px",
  },

  "&:hover": {
    background: "#8B4CF7",
    transform: "scale(1.05)",
  },
  "&:active": { transform: "translateY(0)" },
}));

// ─── Sentinel IDs this component looks for in the DOM ──────────────────────
// 1) "leap-stats-trigger-sentinel"  -> place right after <LeapStatsSection />
//    in page.js. Once this scrolls into view, the sticky bar appears.
// 2) "leap-stick-footer-bottom-sentinel" -> place right before <LeapFooter />
//    (or at the top of LeapFooter) in page.js. Once this scrolls into view,
//    the sticky bar hides itself so it doesn't sit on top of the real footer.
const TRIGGER_SENTINEL_ID = "leap-stats-trigger-sentinel";
const BOTTOM_SENTINEL_ID = "leap-stick-footer-bottom-sentinel";

const LeapStickFooter = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [isVisible, setIsVisible] = useState(false);
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [barHeight, setBarHeight] = useState(0);

  const barRef = useRef(null);

  // ── Measure bar height (for the spacer, so page content doesn't jump) ──
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const updateHeight = () => setBarHeight(el.offsetHeight);
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  // ── Show once the user scrolls past LeapStatsSection ──────────────────
  // Same IntersectionObserver + MutationObserver pattern as AdsFooter:
  // async, batched, never forces layout -> no scroll-jank / flicker.
  useEffect(() => {
    let triggerObserver;
    let mutationObserver;

    const attachObserver = (triggerEl) => {
      triggerObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else if (entry.boundingClientRect.top < 0) {
            // Sentinel already scrolled above viewport -> user is further down the page.
            setIsVisible(true);
          } else {
            // Sentinel still below viewport -> user hasn't reached LeapStatsSection yet.
            setIsVisible(false);
          }
        },
        { threshold: 0, rootMargin: "0px 0px -20% 0px" },
      );
      triggerObserver.observe(triggerEl);
    };

    const existing = document.getElementById(TRIGGER_SENTINEL_ID);
    if (existing) {
      attachObserver(existing);
      return () => triggerObserver?.disconnect();
    }

    mutationObserver = new MutationObserver(() => {
      const el = document.getElementById(TRIGGER_SENTINEL_ID);
      if (el) {
        mutationObserver.disconnect();
        attachObserver(el);
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver?.disconnect();
      triggerObserver?.disconnect();
    };
  }, []);

  // ── Hide once the real LeapFooter comes into view (avoid overlap) ─────
  useEffect(() => {
    let bottomObserver;
    let mutationObserver;

    const attachObserver = (el) => {
      bottomObserver = new IntersectionObserver(
        ([entry]) => setIsNearFooter(entry.isIntersecting),
        { threshold: 0, rootMargin: "0px 0px 0px 0px" },
      );
      bottomObserver.observe(el);
    };

    const existing = document.getElementById(BOTTOM_SENTINEL_ID);
    if (existing) {
      attachObserver(existing);
      return () => bottomObserver?.disconnect();
    }

    mutationObserver = new MutationObserver(() => {
      const el = document.getElementById(BOTTOM_SENTINEL_ID);
      if (el) {
        mutationObserver.disconnect();
        attachObserver(el);
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver?.disconnect();
      bottomObserver?.disconnect();
    };
  }, []);

  const showBar = isVisible && !isNearFooter;

  // ── Smooth-scroll to the LEAP demo form section ────────────────────────
  const scrollToDemoSection = () => {
    const el = document.getElementById("leap-demo-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      {/* Reserves space so page content doesn't jump when the fixed bar appears */}
      <Box
        aria-hidden="true"
        sx={{
          height: showBar ? `${barHeight}px` : 0,
          transition: "height 0.3s ease",
        }}
      />

      <Box
        ref={barRef}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          width: "100%",
          background: "#0B0A10",
          borderTop: "1px solid #0B0A10",
          direction: isRTL ? "rtl" : "ltr",
          opacity: showBar ? 1 : 0,
          pointerEvents: showBar ? "auto" : "none",
          transform: showBar ? "translateY(0)" : "translateY(100%)",
          transition: "opacity 0.3s ease, transform 0.3s ease",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 1.25, sm: 1.5 },
            minHeight: { xs: "52px", sm: "70px" },
            px: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontWeight: 500,
              fontSize: { xs: "12px", sm: "13px", md: "15px" },
              fontFamily: isRTL ? "sans-serif" : "var(--font-outfit)",
              textAlign: "center",
              whiteSpace: { xs: "normal", sm: "nowrap" },
            }}
          >
            {t("LeapStickFooter.tagline", "Meet Getgeek at LEAP 2026, Booth H5.B33")}
          </Typography>

          <CTAButton
            id="btn-leap-sticky-demo"
            // onClick={scrollToDemoSection}
            onClick={() => window.dispatchEvent(new Event("open-leap-demo-modal"))}

            style={{ fontFamily: isRTL ? "sans-serif" : "var(--font-outfit)" }}
          >
            {t("LeapStickFooter.bookDemo", "Book a Live Demo")}
            {isRTL ? (
              <ArrowBackIcon sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }} />
            ) : (
              <ArrowForwardIcon sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }} />
            )}
          </CTAButton>
        </Container>
      </Box>
    </>
  );
};

export default LeapStickFooter;